import { Injectable } from '@angular/core';
import {
    AccountMappingAccountItem,
    IAccountMappingTemplateGroup,
    IAnnualReport,
    ISuperTableReconcileItem,
    ItemType,
    SuperTableRowType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { TemplateType } from '@modeso/types__aumico-lib-templates-be';
import { cloneDeep } from 'lodash-es';
import { applyReclassificationsToFinStatements } from '../../../../aumico-lib-annualreport-fe/src/lib/helpers/applyReclassificationsToFinStatements';
import {
    isEmpty,
    isEmptyRowGroup,
    isFli,
    isGroup,
    isHeader,
    isHeaderGroup,
    isSubfli,
} from '../../../../aumico-lib-templates-fe/src/lib';
import { FX_RESERVE_NODE_NAME, IFxNotesContext, IFxSuperTablePrintContext } from '../interfaces/IFxNotesContext.interface';
import { ISuperTablePrintPreviewLiveTrees } from '../interfaces/super-table-print-preview-live-trees.interface';
import { MappingItemsSelectionNode } from '../interfaces/mapping-items-selection-node.interface';
import { ISuperTableComponentRowFormValue } from '../interfaces/super-table-component-row-form-value.interface';

@Injectable({ providedIn: 'root' })
export class SuperTableMapper {
    /**
     * REV-20899: the reclass-applied selection trees the print/export side needs to recompute
     * super-table row values from the latest AR — the stored row snapshots go stale as soon as
     * reclasses (or phantom reclasses accounts) are added afterwards.
     *
     * FX (REV-21512): with a print context the clones are additionally stamped with `__fx`, so a
     * consumer can read the display-currency twins and rate rows off the same trees; the balance
     * tree then also carries the virtual reserve node.
     *
     * Lives here rather than in each consumer so the notes display components and the Excel
     * export derive identical trees.
     */
    public buildLiveTrees(
        annualReport: IAnnualReport,
        fx: IFxSuperTablePrintContext | null = null,
    ): ISuperTablePrintPreviewLiveTrees | null {
        if (
            annualReport == null ||
            !Array.isArray(annualReport.finStatements) ||
            annualReport.finStatements.length < 2
        ) {
            return null;
        }
        const reclassedFinStatements = annualReport.finStatements.map((fs) => cloneDeep(fs));
        applyReclassificationsToFinStatements(reclassedFinStatements, annualReport.groupingAndMasking);

        const balanceTemplate = reclassedFinStatements[0]?.templateAndAccountsMapping?.template;
        const incomeTemplate = reclassedFinStatements[1]?.templateAndAccountsMapping?.template;

        if (fx != null) {
            if (balanceTemplate?.group) {
                fx.stampTemplateGroup(balanceTemplate.group, 'balance');
            }
            if (incomeTemplate?.group) {
                fx.stampTemplateGroup(incomeTemplate.group, 'income');
            }
        }

        return {
            balance: balanceTemplate?.group
                ? this.buildMappingItemsSelectionTree(balanceTemplate.group, TemplateType.BALANCE, fx?.context)
                : null,
            income: incomeTemplate?.group
                ? this.buildMappingItemsSelectionTree(incomeTemplate.group, TemplateType.INCOME)
                : null,
        };
    }

    public buildMappingItemsSelectionTree(
        group: IAccountMappingTemplateGroup,
        financialType: TemplateType,
        fxContext?: IFxNotesContext | null,
    ): MappingItemsSelectionNode {
        const children: MappingItemsSelectionNode[] = [];

        const allAccounts = this.getAllAccounts(group);
        const accountNumbers = allAccounts.map((a) => a.accountNumber);
        const accountNumberCounts = new Map<string, number>();
        for (const n of accountNumbers) {
            accountNumberCounts.set(n, (accountNumberCounts.get(n) ?? 0) + 1);
        }
        const duplicateAccountNumbers = new Set<string>();
        for (const [n, count] of accountNumberCounts.entries()) {
            if (count > 1) {
                duplicateAccountNumbers.add(n);
            }
        }

        for (const item of group.sortedItems) {
            if (isEmpty(item) || isEmptyRowGroup(item) || isHeader(item) || isHeaderGroup(item)) {
                continue;
            }

            // Recursively add sub-groups

            if (isGroup(item)) {
                children.push(this.buildMappingItemsSelectionTree(item, financialType));
                continue;
            }

            const node: MappingItemsSelectionNode = {
                data: item,
                children: [],
                type: ItemType.GROUP,
                financialType: financialType,
                expanded: true,
                visible: true,
            };

            // If it’s an FLI with accounts, those accounts can also be children
            if (isFli(item)) {
                const fliNode: MappingItemsSelectionNode = {
                    data: item,
                    children: [],
                    type: ItemType.FLI,
                    financialType: financialType,
                    expanded: true,
                    visible: true,
                };
                for (const account of item.accounts) {
                    if (isEmpty(account) || isHeader(account)) {
                        continue;
                    }
                    fliNode.children.push(this.buildAccountNode(account, financialType, duplicateAccountNumbers));
                }
                // REV-20899: phantom reclasses accounts (created in
                // grouping/masking) live on `fli.reclassesAccounts` alongside
                // imported accounts. Surface them as selectable account nodes
                // in the super-table mapping tree so the user can pick them
                // when defining super-table rows.
                const reclassesAccounts = (item as any).reclassesAccounts;
                if (Array.isArray(reclassesAccounts)) {
                    for (const ra of reclassesAccounts) {
                        if (ra == null) continue;
                        fliNode.children.push(
                            this.buildAccountNode(
                                ra as AccountMappingAccountItem,
                                financialType,
                                duplicateAccountNumbers,
                            ),
                        );
                    }
                }

                children.push(fliNode);
            } else {
                children.push(node);
            }
        }

        // FX (REV-21512, R4.2): the translation reserve as a pickable virtual node at the very
        // bottom — it exists only in display currency (functional cells stay empty), its value is
        // the 03d reserve passed via the context. Balance tree only; sentinel name keeps copies
        // re-matchable (G1).
        if (fxContext != null && financialType === TemplateType.BALANCE) {
            children.push({
                data: {
                    name: FX_RESERVE_NODE_NAME,
                    caption: fxContext.reserveLabel,
                    __fx: {
                        displayCY: fxContext.reserveCY,
                        displayPY: fxContext.reservePY,
                        functionalCY: 0,
                        functionalPY: 0,
                    },
                },
                children: [],
                type: ItemType.FLI,
                financialType: financialType,
                expanded: false,
                visible: true,
            });
        }

        return {
            data: group,
            children,
            type: ItemType.GROUP,
            financialType: financialType,
            expanded: true,
            visible: true,
        };
    }

    buildAccountNode(account: AccountMappingAccountItem, financialType: TemplateType, duplicateAccountNumbers: Set<string>): MappingItemsSelectionNode {
        // If the account is itself a subFLI, it might have more nested accounts
        if (isSubfli(account)) {
            const children = account.accounts.map((acc) => this.buildAccountNode(acc, financialType, duplicateAccountNumbers));
            // REV-20899: SubFLIs can also carry phantom reclasses accounts.
            const subReclassesAccounts = (account as any).reclassesAccounts;
            if (Array.isArray(subReclassesAccounts)) {
                for (const ra of subReclassesAccounts) {
                    if (ra == null) continue;
                    children.push(
                        this.buildAccountNode(
                            ra as AccountMappingAccountItem,
                            financialType,
                            duplicateAccountNumbers,
                        ),
                    );
                }
            }
            return {
                data: { ...account },
                children,
                type: ItemType.SUB_FLI,
                financialType: financialType,
                expanded: true,
                visible: true,
            };
        } else {
            // it’s a regular account
            return {
                data: { ...account, isDuplicateAccountNumber: duplicateAccountNumbers.has(account.accountNumber) },
                children: [],
                type: ItemType.ACCOUNT,
                financialType: financialType,
                visible: true,
            };
        }
    }

    public SelectMatchingNodes(
        selectedNodes: MappingItemsSelectionNode[],
        parentNode: MappingItemsSelectionNode,
        deselect?: boolean,
    ): MappingItemsSelectionNode {
        for (const selectedNode of selectedNodes) {
            parentNode = this.selectMatchingNode(selectedNode, parentNode, deselect);
        }

        return parentNode;
    }

    public selectMatchingNode(
        selectedNode: MappingItemsSelectionNode,
        parentNode: MappingItemsSelectionNode,
        deselect?: boolean,
    ): MappingItemsSelectionNode {
        // Check if the current node matches the reconcile item
        if (
            this.getNodeIdentifier(parentNode) === this.getNodeIdentifier(selectedNode) &&
            parentNode.type === selectedNode.type
        ) {
            this.markNodeAndChildrenSelected(parentNode, deselect);
        }

        // Recursively search in children nodes
        if (parentNode.children && parentNode.children.length > 0) {
            for (const child of parentNode.children) {
                this.selectMatchingNode(selectedNode, child, deselect);
            }
        }

        return parentNode; // Return the modified tree
    }

    private markNodeAndChildrenSelected(node: MappingItemsSelectionNode, deselect?: boolean): void {
        if (deselect) {
            node.selected = false;
        } else {
            node.selected = true;
        }

        if (node.children && node.children.length > 0) {
            for (const child of node.children) {
                this.markNodeAndChildrenSelected(child, deselect);
            }
        }
    }

    public getNodeIdentifier(node: MappingItemsSelectionNode): string | undefined {
        if (node.data == null) {
            return undefined;
        }
        if (node.type === ItemType.GROUP) {
            return node.data.name;
        } else if (node.type === ItemType.ACCOUNT && node.data.isDuplicateAccountNumber) {
            return node.data.accountNumber + '-' + node.data.name + '-' + node.data.calculatedClosingBalance + '-' + node.data.calculatedOpeningBalance;
        } else if (node.type === ItemType.ACCOUNT) {
            return node.data.accountNumber;
        } else if ('name' in node.data) {
            return node.data.name;
        }
        return undefined;
    }

    /**
     * Returns true if `potentialAncestor` contains `childNode` somewhere in its subtree.
     * This is used to allow selecting a parent node when children are already selected.
     */
    public isParentNode(
        childNode: MappingItemsSelectionNode | undefined,
        potentialAncestor: MappingItemsSelectionNode | undefined,
    ): boolean {
        if (!childNode || !potentialAncestor) return false;

        const targetId = this.getNodeIdentifier(childNode);
        const targetType = childNode.type;

        const search = (node: MappingItemsSelectionNode | undefined): boolean => {
            if (!node || !node.children || node.children.length === 0) return false;

            for (const child of node.children) {
                const id = this.getNodeIdentifier(child);
                if (id === targetId && child.type === targetType) {
                    return true;
                }
                if (search(child)) return true;
            }

            return false;
        };

        return search(potentialAncestor);
    }



    public findMatchingNodesForReconcileItems(
        reconcileItems: ISuperTableReconcileItem[],
        parentNode: MappingItemsSelectionNode,
    ): Array<MappingItemsSelectionNode | null> {
        return reconcileItems.map((item) => this.findMatchingNodeForReconcileItem(item, parentNode));
    }

    public findMatchingNodeForReconcileItem(
        reconcileItem: ISuperTableReconcileItem,
        parentNode: MappingItemsSelectionNode,
    ): MappingItemsSelectionNode | null {
        // Extracting the relevant identifier from the data object
        const nodeIdentifier = this.getNodeIdentifier(parentNode);

        // Check if the current node matches the reconcile item
        if (nodeIdentifier === reconcileItem.itemName && parentNode.type === reconcileItem.type) {
            return parentNode;
        }

        // Recursively search in children nodes
        if (parentNode.children && parentNode.children.length > 0) {
            for (const child of parentNode.children) {
                const result = this.findMatchingNodeForReconcileItem(reconcileItem, child);
                if (result) return result;
            }
        }

        return null; // If no match found
    }

    public findAutofilledMatchingNodesForReconcileItems(
        reconcileItems: ISuperTableReconcileItem[],
        parentNode: MappingItemsSelectionNode,
    ): Array<MappingItemsSelectionNode | null> {
        return reconcileItems.map((item) => this.findMatchingNodeForReconcileItem(item, parentNode));
    }

    public findAutofilledMatchingNodeForReconcileItem(
        reconcileItem: ISuperTableReconcileItem,
        parentNode: MappingItemsSelectionNode,
    ): MappingItemsSelectionNode | null {
        // Extracting the relevant identifier from the data object
        const currentNodeIdentifier = this.getNodeIdentifier(parentNode);
        const currentNodeIdentifierSplitted = currentNodeIdentifier ? currentNodeIdentifier.split('-') : [];
        const reconcileItemNameSplitted = reconcileItem.itemName ? reconcileItem.itemName.split('-') : [];

        if (reconcileItem.type !== ItemType.ACCOUNT) {
            // Check if the current node matches the reconcile item
            if (currentNodeIdentifier === reconcileItem.itemName && parentNode.type === reconcileItem.type) {
                return parentNode;
            }
        } else if (parentNode.data?.isDuplicateAccountNumber) {
            // Check if the current node matches the reconcile item
            // [0] is accountNumber, [1] is accountName, [2] is calculatedClosingBalance, [3] is calculatedOpeningBalance
            if (currentNodeIdentifierSplitted[0] === reconcileItemNameSplitted[0] && currentNodeIdentifierSplitted[1] === reconcileItemNameSplitted[1] && currentNodeIdentifierSplitted[3] === reconcileItemNameSplitted[2] && parentNode.type === reconcileItem.type) {
                return parentNode;
            }
        } else {
            // Check if the current node matches the reconcile item
            if (currentNodeIdentifier === reconcileItem.itemName && parentNode.type === reconcileItem.type) {
                return parentNode;
            }
        }

        // Recursively search in children nodes
        if (parentNode.children && parentNode.children.length > 0) {
            for (const child of parentNode.children) {
                const result = this.findAutofilledMatchingNodeForReconcileItem(reconcileItem, child);
                if (result) return result;
            }
        }

        return null; // If no match found
    }

    public findMatchingNodesForRowItems(
        row: ISuperTableComponentRowFormValue,
        parentNode: MappingItemsSelectionNode,
    ): MappingItemsSelectionNode[] {
        const mappingItemsSelectionNodes = [];

        // FX (REV-21512): a rate row can reference ANY node kind (group, FLI, account) — its own
        // type is RATE, which no tree node ever carries, so the type check must be skipped.
        const matchType = row.type === SuperTableRowType.RATE ? null : row.type;
        for (const itemId of row.itemsIds) {
            const matchingNode = this.findMatchingNodeForRowItem(itemId, matchType, parentNode);
            if (matchingNode) {
                mappingItemsSelectionNodes.push(matchingNode);
            }
        }

        return mappingItemsSelectionNodes;
    }

    public findMatchingNodeForRowItem(
        itemId: string | number,
        itemType: SuperTableRowType | null,
        parentNode: MappingItemsSelectionNode,
    ): MappingItemsSelectionNode | null {
        // Cast itemId to string if it's a number
        const stringItemId = typeof itemId === 'number' ? itemId + '' : itemId;

        // Extracting the relevant identifier from the data object
        const nodeIdentifier = this.getNodeIdentifier(parentNode);

        // Check if the current node matches the reconcile item (null itemType = match any kind)
        if (nodeIdentifier === stringItemId && (itemType == null || parentNode.type === (itemType as any))) {
            return parentNode;
        }

        // Recursively search in children nodes
        if (parentNode.children && parentNode.children.length > 0) {
            for (const child of parentNode.children) {
                const result = this.findMatchingNodeForRowItem(stringItemId, itemType, child);
                if (result) return result;
            }
        }

        return null; // If no match found
    }

    public findAllMatchingNodesForRowItem(
        itemId: string,
        itemType: SuperTableRowType,
        parentNode: MappingItemsSelectionNode,
    ): MappingItemsSelectionNode[] {
        const matches: MappingItemsSelectionNode[] = [];

        // Extracting the relevant identifier from the data object
        const nodeIdentifier = this.getNodeIdentifier(parentNode);

        // Check if the current node matches the reconcile item
        if (nodeIdentifier === itemId && parentNode.type === (itemType as any)) {
            matches.push(parentNode);
        }

        // Recursively search in children nodes
        if (parentNode.children && parentNode.children.length > 0) {
            for (const child of parentNode.children) {
                const childMatches = this.findAllMatchingNodesForRowItem(itemId, itemType, child);
                matches.push(...childMatches);
            }
        }

        return matches;
    }

    public findAutofilledMatchingNodeForRowItem(
        itemId: string | number,
        itemType: SuperTableRowType,
        parentNode: MappingItemsSelectionNode): MappingItemsSelectionNode | null {

        // Extracting the relevant identifier from the data object
        const currentNodeIdentifier = this.getNodeIdentifier(parentNode);
        const currentNodeIdentifierSplitted = currentNodeIdentifier ? currentNodeIdentifier.split('-') : [];

        // Cast itemId to string if it's a number
        const stringItemId = typeof itemId === 'number' ? itemId + '' : itemId;

        const itemIdSplitted = itemId ? stringItemId.split('-') : [];

        if (itemType !== SuperTableRowType.ACCOUNT) {
            // Check if the current node matches the item
            if (currentNodeIdentifier === stringItemId && parentNode.type === (itemType as any)) {
                return parentNode;
            }
        } else if (parentNode.data?.isDuplicateAccountNumber) {
            // [0] is accountNumber, [1] is accountName, [2] is calculatedClosingBalance, [3] is calculatedOpeningBalance
            if (currentNodeIdentifierSplitted[0] === itemIdSplitted[0] && currentNodeIdentifierSplitted[1] === itemIdSplitted[1] && currentNodeIdentifierSplitted[3] === itemIdSplitted[2] && parentNode.type === (itemType as any)) {
                return parentNode;
            }
        } else {
            // Check if the current node matches the item
            if (currentNodeIdentifier === stringItemId && parentNode.type === (itemType as any)) {
                return parentNode;
            }
        }


        // Recursively search in children nodes
        if (parentNode.children && parentNode.children.length > 0) {
            for (const child of parentNode.children) {
                const result = this.findAutofilledMatchingNodeForRowItem(stringItemId, itemType, child);
                if (result) return result;
            }
        }

        return null; // If no match found
    }

    private getAllAccounts(group: IAccountMappingTemplateGroup): AccountMappingAccountItem[] {
        let accounts: AccountMappingAccountItem[] = [];
        for (const item of group.sortedItems) {
            if (isGroup(item)) {
                accounts = accounts.concat(this.getAllAccounts(item));
            } else if (isFli(item)) {
                for (const account of item.accounts) {
                    if (isSubfli(account)) {
                        accounts = accounts.concat(this.getAccountsFromSubfli(account));
                    } else if (!isEmpty(account) && !isHeader(account)) {
                        accounts.push(account);
                    }
                }
                // REV-20899: phantom reclasses accounts on this FLI count toward
                // the duplicate-account-number check just like imports do, so
                // the super-table's duplicate-aware identifier scheme handles
                // them correctly.
                const reclassesAccounts = (item as any).reclassesAccounts;
                if (Array.isArray(reclassesAccounts)) {
                    for (const ra of reclassesAccounts) {
                        if (ra != null) accounts.push(ra as AccountMappingAccountItem);
                    }
                }
            }
        }
        return accounts;
    }

    private getAccountsFromSubfli(subfli: AccountMappingAccountItem): AccountMappingAccountItem[] {
        let accounts: AccountMappingAccountItem[] = [];
        if (isSubfli(subfli)) {
            accounts.push(subfli);
            for (const account of subfli.accounts) {
                accounts = accounts.concat(this.getAccountsFromSubfli(account));
            }
            // REV-20899: also collect phantom reclasses accounts attached to a
            // SubFLI (rare but possible).
            const subReclassesAccounts = (subfli as any).reclassesAccounts;
            if (Array.isArray(subReclassesAccounts)) {
                for (const ra of subReclassesAccounts) {
                    if (ra != null) accounts.push(ra as AccountMappingAccountItem);
                }
            }
        } else if (!isEmpty(subfli) && !isHeader(subfli)) {
            accounts.push(subfli);
        }
        return accounts;
    }
}
